import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Box, Button, Typography } from '@mui/material';
import * as XLSX from 'xlsx';

import RequirementsListTable from './RequirementsListTable';
import RequirementsFilter from './RequirementsFilter';
import RequirementsCreateDialog from './RequirementsCreateDialog';
import AppPagination from '../../../components/AppPagination';

import { resetRequirementsState } from '../../../slices/requirementsSlice';
import { getRequirementsListAll } from '../../../actions/requirementsActions';

import { createNewRequirement } from '../../../actions/requirementsActions';
import { buildRequirementPayload } from '../../../utils/buildRequirementPayload';
import MessageDialog from '../../../utils/MessageDialog';
import { getSignedFileUrl } from '../../../actions/utilsActions';

// Default filter values for requirements
const defaultFilter = {
  requestNumber: '',
  name: '',
  identification: '',
  city: '',
  client: '',
  center: '',
  status: '',
  companyRef: ''
};

// Component styles
const styles = {
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
};

// Main Requirements component
const Requirements = () => {
  const dispatch = useDispatch();
  // Get company ID from Redux store
  const companyId = useSelector(state => state.auth.profile.companyRef._id);
  // Get requirements list from Redux store
  const requirements = useSelector(state => state.requirements.list);

  // Reference for file input element
  const fileInputRef = useRef(null);

  // State variables
  const [filter, setFilter] = useState({ ...defaultFilter }); // Filter state
  const [pagination, setPagination] = useState({ page: 1 }); // Pagination state
  const [openNew, setOpenNew] = useState(false); // Controls new requirement dialog

  // Dialog state variables
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  // Function to show message dialog
  const showDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };

  // Maps Excel row data to form data structure
  const mapRowToFormData = row => ({
    nombre: row.nombre || '',
    documento: row.documento || '',
    celular: row.celular || '',
    cargo: row.cargo || '',
    correo: row.correo || '',
    ciudad: row.ciudad || '',
    tipoPrueba: row.tipoPrueba || '',
    cliente: row.cliente || '',
    centro: row.centro || '',
    tipoDocumento: row.tipoDocumento || ''
  });

  // Validates form data from Excel import
  const isValidFormData = data => {
    const requiredFields = [
      'nombre',
      'documento',
      'celular',
      'cargo',
      'correo',
      'ciudad',
      'tipoPrueba',
      'cliente',
      'centro',
      'tipoDocumento'
    ];

    // Check if all required fields are present and not empty
    const hasAllFields = requiredFields.every(field => {
      const value = data[field];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });

    if (!hasAllFields) return false;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.correo)) return false;

    // Basic phone number validation (only digits, minimum 7 characters)
    const phoneRegex = /^\d{7,}$/;
    if (!phoneRegex.test(data.celular)) return false;

    // Document type validation
    const validDocumentTypes = ['CC', 'TI', 'CE', 'NIT', 'RC', 'PP'];
    if (!validDocumentTypes.includes(data.tipoDocumento)) return false;

    // Test type validation
    const validTestTypes = ['Específica', 'Pre-empleo', 'Rutina'];
    if (!validTestTypes.includes(data.tipoPrueba)) return false;

    return true;
  };

  /* Function to download template file from firestorage */
  const downloadTemplate = () => {
    const fileUrl = "https://firebasestorage.googleapis.com/v0/b/cristopher-data-451401.firebasestorage.app/o/files%2FCarga%20Masiva%20Requerimientos.xlsx?alt=media&token=81cb1485-1e24-4a3e-a86e-6c0f8da6a2de";

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "Carga Masiva Requerimientos.xlsx"; // File name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Bulk upload from Excel file
  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet); // Convert Excel to JSON

        console.log('Excel JSON:', json);

        const errorRows = [];

        // Process each row
        json.forEach((row, index) => {
          const formData = mapRowToFormData(row);
          if (isValidFormData(formData)) {
            const payload = buildRequirementPayload(formData, companyId);
            dispatch(createNewRequirement({ requirement: payload }))
              .catch(() => {
                errorRows.push(`Fila ${index + 2}: ${JSON.stringify(row)}\n`);
              });
          } else {
            errorRows.push(`Fila ${index + 2}: ${JSON.stringify(row)}\n`);
          }
        });

        // Show alert with errors if any
        if (errorRows.length > 0) {
          showDialog('Advertencia', `❌ Las siguientes filas presentaron errores y no se cargaron:\n\n${errorRows.join('\n')}\n\nPor favor, NO vuelva a cargar todo, sólo las filas con error!.`);
        } else {
          showDialog('Éxito', '✅ Carga masiva completada sin errores');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Trigger file input click
  const handleButtonClick = () => fileInputRef.current.click();

  // Cleanup on component unmount
  useEffect(() => {
    setPagination({ page: 1 });
    return () => dispatch(resetRequirementsState());
  }, []);

  // Download signed URL for test files
  const handleDownloadClick = async (testRef) => {
    const signeUrl = await dispatch(getSignedFileUrl({ query: testRef })).unwrap();
    window.open(signeUrl, '_blank', 'noopener,noreferrer');
  }

  // Fetch requirements based on filters and pagination
  useEffect(() => {
    const f = { page: pagination.page };

    // Add filter values to query if they are not empty
    filter.requestNumber.length > 0 ? f.requestNumber = filter.requestNumber : null;
    filter.name.length > 0 ? f.name = filter.name : null;
    filter.identification.length > 0 ? f.identification = filter.identification : null;
    filter.city.length > 0 ? f.city = filter.city : null;
    filter.client.length > 0 ? f.client = filter.client : null;
    filter.center.length > 0 ? f.center = filter.center : null;
    filter.status.length > 0 ? f.status = filter.status : null;
    f.companyRef = companyId;

    // Dispatch action to get requirements with filters
    dispatch(getRequirementsListAll({ query: f }));
  }, [pagination]);

  // Event handlers
  const handleChange = e => setFilter({ ...filter, [e.target.name]: e.target.value });
  const onSubmit = e => {
    e.preventDefault();
    setPagination({ page: 1 }); // Reset to first page when applying filters
  };
  const onClear = () => setFilter({ ...defaultFilter }); // Clear all filters
  const nextPage = () => setPagination({ page: pagination.page + 1 }); // Go to next page
  const prevPage = () => setPagination({ page: Math.max(pagination.page - 1, 1) }); // Go to previous page

  const handleOpenNew = () => setOpenNew(true); // Open new requirement dialog
  const handleCloseNew = () => setOpenNew(false); // Close new requirement dialog

  return (
    <>
      <Helmet>
        <title>Cristopher | Requerimientos</title>
      </Helmet>

      <Box sx={styles.title}>
        <Typography variant="h5" fontWeight="bold">Requerimientos</Typography>
        <Box display="flex" gap={1}>
          <Button onClick={handleOpenNew}>Solicitar Prueba</Button>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <Button onClick={handleButtonClick} color="primary">Solicitar Masivo</Button>
          <Button onClick={downloadTemplate} color="primary">Descargar plantilla</Button>
        </Box>
      </Box>

      <RequirementsFilter {...filter} onChange={handleChange} onSubmit={onSubmit} onClear={onClear} />
      <RequirementsListTable requirements={requirements} onDownload={handleDownloadClick} />
      <AppPagination page={pagination.page} prev={prevPage} next={nextPage} />
      <RequirementsCreateDialog open={openNew} onClose={handleCloseNew} companyRef={companyId} />

      <MessageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        message={dialogMessage}
      />
    </>
  );
};

export default Requirements;